import { HttpErrorResponse } from '@angular/common/http';
import { ElementRef } from '@angular/core';
import Quill from 'quill';

export class AppTeamUtil
{
    public static tryParseErrorText(error: HttpErrorResponse): { hasError: boolean, errorText: string }
    {
        let errorText = '';

        if (error.error && error.error.Error_1 && error.error.Error_1[0])
        {
            errorText = error.error.Error_1[0];
        }

        return { hasError: errorText !== '', errorText: errorText };
    }

    public static getUtcDate(dateString: string)
    {
        if (!dateString)
        { return null; }

        return new Date(dateString.endsWith('Z') ? dateString : `${dateString}Z`);
    }

    public static sortByName(items: any[])
    {
        return items.sort((a, b) =>
        {
            const aName = a.name || '';
            const bName = b.name || '';
            return aName !== bName ? (aName.toLowerCase() < bName.toLowerCase() ? -1 : 1) : 0;
        });
    }

    public static getParsedFloat(numberString: string)
    {
        const cleanValue = numberString.replace(/[^0-9.]/g, '');
        return parseFloat(cleanValue);
    }

    public static capitalizeWord(word: string)
    {
        if (word.length === 0)
        { return word; }
        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    public static getFileExtension(filename: string): string
    {
        return filename.slice(filename.lastIndexOf('.')).toLowerCase();
    }

    public static disableLastpass(parentElement: ElementRef)
    {
        const inputElements = parentElement.nativeElement.getElementsByTagName('input');
        for (const element of inputElements)
        {
            element.setAttribute('data-lpignore', 'true');
        }
    }

    public static convertDateToApiDate(date: Date | undefined): Date | undefined
    {
        if (date == null) { return undefined; }

        const newDate = new Date(date);
        const hourTimezoneDifference = -newDate.getTimezoneOffset() / 60;
        newDate.setHours(newDate.getHours() + hourTimezoneDifference);
        return newDate;
    }

    public static convertApiDateToDate(date?: Date): Date | undefined
    {
        if (date == undefined) return undefined;

        const newDate = new Date(date);
        const hourTimezoneDifference = newDate.getTimezoneOffset() / 60;
        newDate.setHours(newDate.getHours() + hourTimezoneDifference);
        return newDate;
    }

    public static convertApiDateStringToDate(dateString: string | Date): Date | undefined
    {
        if (dateString == null) return undefined;
        if (dateString instanceof Date) return dateString;
        return new Date(dateString.replace(/-/g, '/').replace('T', ' ').replace(/\..*|\+.*/, ''));
    }

    public static groupBy(list: any, keyGetter: any): Map<any, any>
    {
        const map = new Map();
        list.forEach((item: any) =>
        {
            const key = keyGetter(item);
            const collection = map.get(key);
            if (!collection)
            {
                map.set(key, [item]);
            }
            else
            {
                collection.push(item);
            }
        });
        return map;
    }

    public static getCommonElements(arrays: any[]): any[]
    {
        let commonElements: any[] = [];
        const allButFirstArray = arrays.slice(1);
        arrays[0].forEach((element: any) =>
        {
            if (commonElements.includes(element)) return; // Just in case there are duplicates

            const isCommon = allButFirstArray.every(a => a.includes(element));
            if (isCommon) commonElements.push(element);
        });
        return commonElements;
    }

    public static hasForCount(count: number): string { return count === 1 ? 'has' : 'have'; }
    public static isForCount(count: number): string { return count === 1 ? 'is' : 'are'; }

    // Magnitude should be positive to lighten a color and should be negative to darken a color (-256 to 256)
    public static lightenOrDarkenColor(hexColor: string, magnitude: number): string
    {
        hexColor = hexColor.replace(`#`, ``);
        if (hexColor.length !== 6) return hexColor; // can't transform
        const decimalColor = parseInt(hexColor, 16);
        // const r = (decimalColor >> 16) * (1 - magnitude);
        // const g = (decimalColor & 0x0000ff) * (1 - magnitude);
        // const b = ((decimalColor >> 8) & 0x00ff) * (1 - magnitude);
        let r = (decimalColor >> 16);
        let g = (decimalColor & 0x0000ff);
        let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
        r = r + (255 - r) * magnitude;
        g = g + (255 - g) * magnitude;
        b = b + (255 - b) * magnitude;
        // let r = (decimalColor >> 16) + magnitude;
        // r > 255 && (r = 255);
        // r < 0 && (r = 0);
        // let g = (decimalColor & 0x0000ff) + magnitude;
        // g > 255 && (g = 255);
        // g < 0 && (g = 0);
        // let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
        // b > 255 && (b = 255);
        // b < 0 && (b = 0);
        return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
    }

    static htmlTextFromString(str?: string): string
    {
        if (str == undefined || str.length === 0) return '';
        const lines: string[] = str.split('\n');
        const htmlLines = lines.map(line => `<p>${line}</p>`);
        return htmlLines.join('');
    }

    static configureQuillClipboard(quill: any)
    {
        // Suppress formatting of all pasted text
        // this.quill.clipboard.addMatcher (Node.ELEMENT_NODE, function (node: any, delta: any) {
        // 	const plaintext = node.innerText;
        // 	const Delta = Quill.import('delta');
        // 	return new Delta().insert(plaintext)
        // });

        // Don't allow images to be pasted
        const Delta = Quill.import('delta');
        quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node: any, delta: any) {
            if (node.src != null && node.src.indexOf('data:image') !== -1)
            {
                return new Delta().insert('');
            }
            return delta;
        });
    }

    static flashElement(id: string, durationInMS: number): void
    {
        const element = document.getElementById(id);
        if (element == null) return;
        let remainingDuration = durationInMS;
        const interval = Math.min(durationInMS / 3, 500);
        const intervalId = setInterval(() => {
            element.style.visibility = element.style.visibility === "hidden" ? "visible" : "hidden";
            remainingDuration -= interval;
            if (remainingDuration <= 0) {
                clearInterval(intervalId);
                element.style.visibility = "visible";
            }
        }, interval);
    }
}
